import React, { useState } from 'react';
import {
  Upload,
  FileSpreadsheet,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Info,
  Loader,
  Download,
  History,
  Trash2,
  Eye
} from 'lucide-react';
import { useProject } from '../../contexts/ProjectContext';
import api from '../../api';
import toast from 'react-hot-toast';

interface QCRule {
  ruleId: number;
  ruleName: string;
  status: 'Pass' | 'Fail' | 'N/A' | 'Info';
  details: string | null;
  affectedColumns?: string[] | null;
}

interface QCResults {
  summary: {
    totalRules: number;
    passed: number;
    failed: number;
    na: number;
    info: number;
  };
  rules: QCRule[];
  metadata: {
    validatedAt: string;
    filename: string;
    projectId: string;
    userId: string;
  };
}

interface QCHistoryItem {
  id: number;
  filename: string;
  validatedAt: string;
  fileFormat: string;
  summary: {
    totalRules: number;
    passed: number;
    failed: number;
    na: number;
    info: number;
  };
  validationStatus: string;
  qualityScore: number | null;
}

const RiskTemplateQCTab: React.FC = () => {
  const { currentProject } = useProject();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [qcResults, setQcResults] = useState<QCResults | null>(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<QCHistoryItem[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedHistoryId, setSelectedHistoryId] = useState<number | null>(null);

  // Load latest QC results and history on mount
  React.useEffect(() => {
    const loadData = async () => {
      if (!currentProject?.id) return;

      try {
        setLoading(true);
        setLoadingHistory(true);

        // Load latest results
        const latestResponse = await api.get(`/api/risk-template-qc/${currentProject.id}/latest`);
        if (latestResponse.data.results) {
          setQcResults(latestResponse.data.results);
        }

        // Load history
        const historyResponse = await api.get(`/api/risk-template-qc/${currentProject.id}/history?limit=20`);
        if (historyResponse.data.history) {
          setHistory(historyResponse.data.history);
        }
      } catch (error) {
        console.error('Failed to load QC data:', error);
      } finally {
        setLoading(false);
        setLoadingHistory(false);
      }
    };

    loadData();
  }, [currentProject?.id]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validExtensions = ['.xlsx', '.xls'];
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

      if (!validExtensions.includes(fileExtension)) {
        toast.error('Please upload an Excel file (.xlsx or .xls)');
        return;
      }

      setSelectedFile(file);
      setQcResults(null);
    }
  };

  const handleUploadAndValidate = async () => {
    if (!selectedFile || !currentProject) {
      toast.error('Please select a file and ensure a project is selected');
      return;
    }

    try {
      setUploading(true);

      const formData = new FormData();
      formData.append('template', selectedFile);
      formData.append('projectId', currentProject.id.toString());

      toast.loading('Uploading and validating template...', { id: 'qc-upload' });

      const response = await api.post('/api/risk-template-qc/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setQcResults(response.data.results);

      toast.success(
        `QC Validation Complete! ${response.data.results.summary.passed}/${response.data.results.summary.totalRules} rules passed`,
        { id: 'qc-upload', duration: 5000 }
      );

    } catch (error: any) {
      console.error('Upload error:', error);
      toast.error(
        error.response?.data?.error || 'Failed to validate template',
        { id: 'qc-upload' }
      );
    } finally {
      setUploading(false);
    }
  };

  const handleExportResults = async (format: 'excel' | 'pdf' = 'excel') => {
    if (!qcResults) {
      toast.error('No QC results to export');
      return;
    }

    try {
      toast.loading(`Exporting QC results to ${format.toUpperCase()}...`, { id: 'qc-export' });

      const response = await api.post('/api/risk-template-qc/export', {
        results: qcResults,
        format
      }, {
        responseType: 'blob'
      });

      const filename = `QC_Results_${qcResults.metadata.filename}_${Date.now()}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`QC results exported successfully!`, { id: 'qc-export' });

    } catch (error: any) {
      console.error('Export error:', error);
      toast.error(
        error.response?.data?.error || 'Failed to export QC results',
        { id: 'qc-export' }
      );
    }
  };

  const handleViewHistoryItem = async (validationId: number) => {
    try {
      setLoading(true);
      setSelectedHistoryId(validationId);

      const response = await api.get(`/api/risk-template-qc/validation/${validationId}`);
      if (response.data.results) {
        setQcResults(response.data.results);
      }
    } catch (error: any) {
      console.error('Failed to load validation details:', error);
      toast.error('Failed to load validation details');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteValidation = async (validationId: number) => {
    if (!confirm('Are you sure you want to delete this QC validation?')) {
      return;
    }

    try {
      toast.loading('Deleting validation...', { id: 'qc-delete' });

      await api.delete(`/api/risk-template-qc/validation/${validationId}`);

      // Remove from history
      setHistory(prev => prev.filter(item => item.id !== validationId));

      // If deleted item was currently selected, load latest
      if (selectedHistoryId === validationId) {
        const latestResponse = await api.get(`/api/risk-template-qc/${currentProject?.id}/latest`);
        if (latestResponse.data.results) {
          setQcResults(latestResponse.data.results);
          setSelectedHistoryId(null);
        } else {
          setQcResults(null);
          setSelectedHistoryId(null);
        }
      }

      toast.success('Validation deleted successfully', { id: 'qc-delete' });
    } catch (error: any) {
      console.error('Failed to delete validation:', error);
      toast.error('Failed to delete validation', { id: 'qc-delete' });
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Pass':
        return <CheckCircle2 className="w-4 h-4 text-green-600" />;
      case 'Fail':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'N/A':
        return <AlertCircle className="w-4 h-4 text-gray-400" />;
      case 'Info':
        return <Info className="w-4 h-4 text-blue-600" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status: string) => {
    const baseClasses = 'px-2 py-0.5 rounded text-xs font-medium';

    switch (status) {
      case 'Pass':
        return <span className={`${baseClasses} bg-green-100 text-green-800`}>✓ Pass</span>;
      case 'Fail':
        return <span className={`${baseClasses} bg-red-100 text-red-800`}>✗ Fail</span>;
      case 'N/A':
        return <span className={`${baseClasses} bg-gray-100 text-gray-600`}>N/A</span>;
      case 'Info':
        return <span className={`${baseClasses} bg-blue-100 text-blue-800`}>ⓘ Info</span>;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Upload Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <FileSpreadsheet className="w-5 h-5 text-blue-600" />
          Upload Risk Template for Quality Check
        </h3>

        <div className="space-y-4">
          {/* File Input */}
          <div className="flex items-center gap-4">
            <label
              htmlFor="qc-file-upload"
              className="flex-1 cursor-pointer border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-blue-500 transition-colors"
            >
              <div className="flex flex-col items-center justify-center text-center">
                <Upload className="w-10 h-10 text-gray-400 mb-2" />
                {selectedFile ? (
                  <>
                    <p className="text-sm font-medium text-gray-900 mb-1">
                      {selectedFile.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-medium text-gray-900 mb-1">
                      Click to upload Excel template
                    </p>
                    <p className="text-xs text-gray-500">
                      .xlsx or .xls - Max 10MB
                    </p>
                  </>
                )}
              </div>
              <input
                id="qc-file-upload"
                type="file"
                className="hidden"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
              />
            </label>
          </div>

          {/* Upload Button */}
          <button
            onClick={handleUploadAndValidate}
            disabled={!selectedFile || uploading || !currentProject}
            className="w-full bg-blue-600 text-white px-4 py-2.5 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {uploading ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                <span>Validating Template...</span>
              </>
            ) : (
              <>
                <CheckCircle2 className="w-5 h-5" />
                <span>Run Quality Check</span>
              </>
            )}
          </button>
        </div>
      </div>

      {qcResults && (
        <>
          <div className="grid grid-cols-5 gap-3">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
              <p className="text-xs text-gray-600 mb-1">Total Rules</p>
              <p className="text-xl font-bold text-gray-900">{qcResults.summary.totalRules}</p>
            </div>

            <div className="bg-green-50 rounded-lg shadow-sm border border-green-200 p-3">
              <p className="text-xs text-green-700 mb-1">Passed</p>
              <p className="text-xl font-bold text-green-900">{qcResults.summary.passed}</p>
            </div>

            <div className="bg-red-50 rounded-lg shadow-sm border border-red-200 p-3">
              <p className="text-xs text-red-700 mb-1">Failed</p>
              <p className="text-xl font-bold text-red-900">{qcResults.summary.failed}</p>
            </div>

            <div className="bg-gray-50 rounded-lg shadow-sm border border-gray-200 p-3">
              <p className="text-xs text-gray-600 mb-1">N/A</p>
              <p className="text-xl font-bold text-gray-900">{qcResults.summary.na}</p>
            </div>

            <div className="bg-blue-50 rounded-lg shadow-sm border border-blue-200 p-3">
              <p className="text-xs text-blue-700 mb-1">Info</p>
              <p className="text-xl font-bold text-blue-900">{qcResults.summary.info}</p>
            </div>
          </div>

          {/* Results Table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-blue-600" />
                    QC Validation Results
                  </h3>
                  <p className="text-xs text-gray-600 mt-1">
                    {qcResults.metadata.filename} • {new Date(qcResults.metadata.validatedAt).toLocaleString()}
                  </p>
                </div>
                <button
                  onClick={() => handleExportResults('excel')}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Export to Excel
                </button>
              </div>
            </div>

            <div className="overflow-x-auto max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                      Rule
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ECM Quality Rule
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                      Result
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {qcResults.rules.map((rule) => (
                    <tr
                      key={rule.ruleId}
                      className={`hover:bg-gray-50 ${
                        rule.status === 'Fail' ? 'bg-red-50' :
                        rule.status === 'Pass' ? 'bg-green-50' : ''
                      }`}
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        {rule.ruleId}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <div>
                          <p className="font-medium">{rule.ruleName}</p>
                          {rule.details && (
                            <p className="text-xs text-gray-600 mt-1 bg-gray-100 p-2 rounded">
                              {rule.details}
                            </p>
                          )}
                          {rule.affectedColumns && rule.affectedColumns.length > 0 && (
                            <p className="text-xs text-orange-600 mt-1">
                              Affected: {rule.affectedColumns.join(', ')}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(rule.status)}
                          {getStatusBadge(rule.status)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* QC History Section */}
      {history.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <History className="w-5 h-5 text-blue-600" />
              Previous QC Tests
            </h3>
            <p className="text-xs text-gray-600 mt-1">
              Click on any test to view details
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Filename
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Passed
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Failed
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Quality
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loadingHistory ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center">
                      <Loader className="w-6 h-6 animate-spin mx-auto text-gray-400" />
                    </td>
                  </tr>
                ) : (
                  history.map((item, index) => (
                    <tr
                      key={item.id}
                      onClick={() => handleViewHistoryItem(item.id)}
                      className={`hover:bg-gray-50 cursor-pointer transition-colors ${
                        selectedHistoryId === item.id ? 'bg-blue-50' : ''
                      } ${index === 0 ? 'bg-green-50/30' : ''}`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <FileSpreadsheet className="w-4 h-4 text-gray-400" />
                          <div>
                            <p className="font-medium text-gray-900">{item.filename}</p>
                            {index === 0 && (
                              <span className="text-xs text-green-700 font-medium">Latest</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {new Date(item.validatedAt).toLocaleString()}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-green-700 font-medium">{item.summary.passed}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-red-700 font-medium">{item.summary.failed}</span>
                      </td>
                      <td className="px-4 py-3">
                        {item.qualityScore !== null && item.qualityScore !== undefined ? (
                          <span className={`font-medium ${
                            Number(item.qualityScore) >= 80 ? 'text-green-700' :
                            Number(item.qualityScore) >= 60 ? 'text-yellow-700' :
                            'text-red-700'
                          }`}>
                            {Number(item.qualityScore).toFixed(0)}%
                          </span>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleViewHistoryItem(item.id)}
                            className="text-blue-600 hover:text-blue-800 p-1.5 rounded hover:bg-blue-50 transition-colors"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteValidation(item.id)}
                            className="text-red-600 hover:text-red-800 p-1.5 rounded hover:bg-red-50 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default RiskTemplateQCTab;
